// Character Loader - Loads and manages animated GLB character
import * as THREE from 'three';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';
import { DRACOLoader } from 'three/addons/loaders/DRACOLoader.js';

export interface CharacterAnimations {
    idle?: THREE.AnimationClip;
    walk?: THREE.AnimationClip;
    point?: THREE.AnimationClip;
    celebrate?: THREE.AnimationClip;
    wave?: THREE.AnimationClip;
}

export interface LoadedCharacter {
    scene: THREE.Group;
    mixer: THREE.AnimationMixer;
    animations: CharacterAnimations;
    playAnimation: (name: keyof CharacterAnimations, options?: { loop?: boolean; fadeIn?: number }) => void;
    stopAnimation: (name: keyof CharacterAnimations, fadeOut?: number) => void;
    update: (delta: number) => void;
}

export class CharacterLoader {
    private static loader: GLTFLoader;
    private static dracoLoader: DRACOLoader;
    private static cache: Map<string, LoadedCharacter> = new Map();
    private static isInitialized = false;

    private static init(): void {
        if (this.isInitialized) return;

        // Create and configure DRACOLoader
        this.dracoLoader = new DRACOLoader();
        this.dracoLoader.setDecoderPath('https://www.gstatic.com/draco/versioned/decoders/1.5.7/');

        // Create GLTFLoader and set DRACOLoader
        this.loader = new GLTFLoader();
        this.loader.setDRACOLoader(this.dracoLoader);

        this.isInitialized = true;
    }

    static async load(
        url: string,
        extraAnimations: Record<string, string> = {},
        materialColors: Record<string, number> = {} // New: Map of material name part -> hex color
    ): Promise<LoadedCharacter> {
        this.init();

        // Check cache (key includes extras)
        const cacheKey = url + JSON.stringify(extraAnimations) + JSON.stringify(materialColors);
        if (this.cache.has(cacheKey)) {
            return this.cache.get(cacheKey)!;
        }

        return new Promise((resolve, reject) => {
            this.loader.load(
                url,
                async (gltf) => {
                    const scene = gltf.scene;
                    const mixer = new THREE.AnimationMixer(scene);

                    // Set up character
                    scene.traverse((child) => {
                        if ((child as THREE.Mesh).isMesh) {
                            child.castShadow = true;
                            child.receiveShadow = true;
                            // Hide the "Cube" bounding box artifact
                            if (child.name === 'Cube' || child.name.includes('Box')) {
                                child.visible = false;
                                return;
                            }

                            console.log('Character Mesh:', child.name);

                            // Apply skin color change and fix lighting materials
                            const mesh = child as THREE.Mesh;
                            if (mesh.material) {
                                const materials = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
                                materials.forEach((mat) => {
                                    const m = mat as THREE.MeshStandardMaterial;
                                    console.log('Material:', m.name, 'Map:', m.map ? 'Yes' : 'No');

                                    // Reverting roughness/metalness override to see if that fixes visibility
                                    // m.roughness = 0.8; 
                                    // m.metalness = 0.0;
                                    // m.needsUpdate = true; // Only update if we change something

                                    const matName = m.name.toLowerCase();

                                    // Identify if this is a skin/body part
                                    const isSkin = matName.includes('skin') || matName.includes('body') || matName.includes('head');

                                    // Check for custom overrides first
                                    let targetColor = null;
                                    for (const [key, color] of Object.entries(materialColors)) {
                                        if (matName.includes(key.toLowerCase())) {
                                            targetColor = color;
                                            break;
                                        }
                                    }

                                    // If it's skin, apply Texture Repainting
                                    if (isSkin && m.map && m.map.image) {
                                        const originalTexture = m.map;

                                        // Wait for image to load if needed (though usually loaded by GLTFLoader)
                                        const image = originalTexture.image as HTMLImageElement;
                                        const canvas = document.createElement('canvas');
                                        canvas.width = image.width;
                                        canvas.height = image.height;
                                        const ctx = canvas.getContext('2d');

                                        if (ctx) {
                                            ctx.drawImage(image, 0, 0);
                                            const imgData = ctx.getImageData(0, 0, canvas.width, canvas.height);
                                            const data = imgData.data;

                                            // Pixel Processing Parameters
                                            // Skin is typically Warm (Hue 0-50), and in this texture, it is Dark.
                                            // We want to lighten "Dark Brown" pixels but avoid "Red" (Headphones) or "Orange" (Pants).

                                            // Helper: RGB to HSL
                                            const rgbToHsl = (r: number, g: number, b: number) => {
                                                r /= 255; g /= 255; b /= 255;
                                                const max = Math.max(r, g, b), min = Math.min(r, g, b);
                                                let h = 0, s = 0, l = (max + min) / 2;
                                                if (max !== min) {
                                                    const d = max - min;
                                                    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
                                                    switch (max) {
                                                        case r: h = (g - b) / d + (g < b ? 6 : 0); break;
                                                        case g: h = (b - r) / d + 2; break;
                                                        case b: h = (r - g) / d + 4; break;
                                                    }
                                                    h /= 6;
                                                }
                                                return [h * 360, s, l];
                                            };

                                            // Helper: HSL to RGB
                                            const hslToRgb = (h: number, s: number, l: number) => {
                                                let r, g, b;
                                                if (s === 0) {
                                                    r = g = b = l;
                                                } else {
                                                    const hue2rgb = (p: number, q: number, t: number) => {
                                                        if (t < 0) t += 1;
                                                        if (t > 1) t -= 1;
                                                        if (t < 1 / 6) return p + (q - p) * 6 * t;
                                                        if (t < 1 / 2) return q;
                                                        if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
                                                        return p;
                                                    };
                                                    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
                                                    const p = 2 * l - q;
                                                    h /= 360;
                                                    r = hue2rgb(p, q, h + 1 / 3);
                                                    g = hue2rgb(p, q, h);
                                                    b = hue2rgb(p, q, h - 1 / 3);
                                                }
                                                return [Math.round(r * 255), Math.round(g * 255), Math.round(b * 255)];
                                            };

                                            let pixelCount = 0;
                                            for (let i = 0; i < data.length; i += 4) {
                                                const r = data[i];
                                                const g = data[i + 1];
                                                const b = data[i + 2];

                                                const [h, s, l] = rgbToHsl(r, g, b);

                                                // THRESHOLDS TUNING V3 - Fix "Patches" (Missed Highlights/Edges)
                                                // 1. Widen Hue (4-60) to catch all skin variants and edge bleeding.
                                                // 2. Increase Lightness Max (< 0.75) to catch skin SPECULAR HIGHLIGHTS that were being missed (causing patches).
                                                // 3. Add Saturation Max (< 0.65) to protect vibrant Orange Pants/Red Headphones.
                                                //    Skin is usually less saturated than anime clothes.

                                                if (h > 4 && h < 60 && l < 0.75 && l > 0.01 && s > 0.05 && s < 0.65) {
                                                    // Target: Stable Light Peach
                                                    // Normalize Hue to 25 (Nice Peach) to handle reddish/yellowish unevenness
                                                    const targetH = 25;

                                                    // Boost Lightness but clamp it to avoid "nuclear white"
                                                    // Map 0.0-0.75 -> 0.80-0.95
                                                    // We use a non-linear curve to lift dark areas MORE than light areas (flattening the shadows)
                                                    let newL = 0.80 + (l * 0.2);
                                                    if (newL > 0.95) newL = 0.95;

                                                    // Standardize Saturation for even tone
                                                    // Previously we forced 0.6 which might be too "Yellow/Cartoon". Lowering to 0.45 for "Natural".
                                                    const newS = 0.45;

                                                    const [newR, newG, newB] = hslToRgb(targetH, newS, newL);

                                                    data[i] = newR;
                                                    data[i + 1] = newG;
                                                    data[i + 2] = newB;
                                                    pixelCount++;
                                                }
                                            }
                                            console.log(`Repainted ${pixelCount} skin pixels.`);

                                            ctx.putImageData(imgData, 0, 0);
                                            const newTexture = new THREE.CanvasTexture(canvas);
                                            newTexture.flipY = false;
                                            newTexture.colorSpace = THREE.SRGBColorSpace;
                                            newTexture.needsUpdate = true;

                                            m.map = newTexture;
                                        }

                                        // Reset material physics
                                        m.color.setHex(0xFFFFFF);
                                        m.roughness = 0.7; // Balanced
                                        m.metalness = 0.0;
                                        m.vertexColors = false;
                                        m.emissive.setHex(0x000000);
                                        m.emissiveIntensity = 0;

                                        m.needsUpdate = true;
                                        return;
                                    }

                                    // For non-skin parts, apply custom color if found
                                    if (targetColor !== null) {
                                        m.color.setHex(targetColor);
                                    }
                                });
                            }
                        }
                    });

                    // Map animations by name
                    const animations: CharacterAnimations = {};
                    const animationActions: Map<string, THREE.AnimationAction> = new Map();

                    // Helper to add animation
                    const addAnimation = (clip: THREE.AnimationClip, type?: keyof CharacterAnimations) => {
                        const name = clip.name.toLowerCase();
                        let targetType = type;

                        if (!targetType) {
                            if (name.includes('idle') || name.includes('stand')) targetType = 'idle';
                            else if (name.includes('walk') || name.includes('run')) targetType = 'walk';
                            else if (name.includes('point') || name.includes('gesture')) targetType = 'point';
                            else if (name.includes('celeb') || name.includes('dance')) targetType = 'celebrate';
                            else if (name.includes('wave') || name.includes('greeting')) targetType = 'wave';
                        }

                        if (targetType) {
                            animations[targetType] = clip;
                            animationActions.set(targetType, mixer.clipAction(clip));
                        }
                    };

                    // Process main file animations
                    gltf.animations.forEach((clip) => addAnimation(clip));

                    // Load extra animations
                    if (extraAnimations) {
                        const loadPromises = Object.entries(extraAnimations).map(([type, animUrl]) => {
                            return new Promise<void>((resolveAnim) => {
                                this.loader.load(animUrl, (animGltf) => {
                                    if (animGltf.animations.length > 0) {
                                        // Take the first animation from the file
                                        const clip = animGltf.animations[0];
                                        // Rename it to avoid conflicts (optional)
                                        clip.name = type;
                                        addAnimation(clip, type as keyof CharacterAnimations);
                                    }
                                    resolveAnim();
                                }, undefined, () => resolveAnim()); // Ignore errors for extras
                            });
                        });
                        await Promise.all(loadPromises);
                    }

                    // If no idle found, use first available
                    if (!animations.idle && gltf.animations.length > 0) {
                        animations.idle = gltf.animations[0];
                        animationActions.set('idle', mixer.clipAction(gltf.animations[0]));
                    }

                    let currentAction: THREE.AnimationAction | null = null;

                    const character: LoadedCharacter = {
                        scene,
                        mixer,
                        animations,

                        playAnimation(name, options = {}) {
                            const action = animationActions.get(name);
                            if (!action) return;

                            const { loop = true, fadeIn = 0.3 } = options;

                            if (currentAction && currentAction !== action) {
                                currentAction.fadeOut(fadeIn);
                            }

                            action.reset();
                            action.setLoop(loop ? THREE.LoopRepeat : THREE.LoopOnce, loop ? Infinity : 1);
                            action.clampWhenFinished = !loop;
                            action.fadeIn(fadeIn);
                            action.play();

                            currentAction = action;
                        },

                        stopAnimation(name, fadeOut = 0.3) {
                            const action = animationActions.get(name);
                            if (action) {
                                action.fadeOut(fadeOut);
                            }
                        },

                        update(delta) {
                            mixer.update(delta);
                        }
                    };

                    this.cache.set(cacheKey, character);
                    resolve(character);
                },
                undefined,
                (error) => {
                    console.error('Error loading character:', error);
                    reject(error);
                }
            );
        });
    }
}
